import { useTranslations } from "next-intl";
import { Fragment, useState } from "react";

import { type SearchPassengers } from "@trip/constants";

import { MinusIcon } from "~/components/icons/minus-icon";
import { PlusIcon } from "~/components/icons/plus-icon";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

export function usePassengerTriggerLabel() {
  const t = useTranslations("Common");
  return (value: SearchPassengers) => {
    const strings: string[] = [];
    if (value.adults) {
      strings.push(t("adult", { count: value.adults }));
    }
    if (value.children.length > 0) {
      strings.push(t("child", { count: value.children.length }));
    }
    return strings.join(", ");
  };
}

export function Passengers({
  value,
  onChange,
}: {
  onChange: (value: SearchPassengers) => void;
  value: SearchPassengers;
}) {
  const tCommon = useTranslations("Common");
  const [input, setInput] = useState({
    adults: value.adults,
    children: [...value.children],
  });
  function onInputChange(value: SearchPassengers) {
    onChange(value);
    setInput(value);
  }
  return (
    <>
      <div className="font-medium">{tCommon("adults")}</div>
      <div className="flex items-center">
        <Button
          disabled={input.adults < 2}
          onClick={(event) => {
            event.preventDefault();
            onInputChange({ ...input, adults: input.adults - 1 });
          }}
          size="circle"
        >
          <MinusIcon />
        </Button>
        <div className="px-4 tabular-nums">{input.adults}</div>
        <Button
          onClick={(event) => {
            event.preventDefault();
            onInputChange({ ...input, adults: input.adults + 1 });
          }}
          size="circle"
        >
          <PlusIcon />
        </Button>
      </div>
      <div className="font-medium">{tCommon("children")}</div>
      <div className="flex items-center">
        <Button
          disabled={input.children.length === 0}
          onClick={(event) => {
            event.preventDefault();
            onInputChange({
              ...input,
              children: input.children.slice(0, input.children.length - 1),
            });
          }}
          size="circle"
        >
          <MinusIcon />
        </Button>
        <div className="px-4 tabular-nums">{input.children.length}</div>
        <Button
          onClick={(event) => {
            event.preventDefault();
            onInputChange({
              ...input,
              children: [...input.children, { age: 10 }],
            });
          }}
          size="circle"
        >
          <PlusIcon />
        </Button>
      </div>
      {input.children.map(({ age }, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <div className="font-medium">
            {tCommon("childAge", { number: index + 1 })}
          </div>
          <div>
            <Select
              onValueChange={(ageString) => {
                const age = Number.parseInt(ageString, 10);
                onInputChange({
                  ...input,
                  children: input.children.map((child, i) =>
                    i === index ? { age } : child,
                  ),
                });
              }}
              value={age.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder={tCommon("age")} />
              </SelectTrigger>
              <SelectContent align="center">
                {Array.from({ length: 18 }).map((_value, index) => (
                  <SelectItem
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={index}
                    value={index.toString()}
                  >
                    {tCommon("yearsOld", { age: index })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Fragment>
      ))}
    </>
  );
}
